import { Popover, Transition } from "@headlessui/react"
import classNames from "classnames"
import type { FC, ReactNode } from "react"
import { Fragment, useRef } from "react"
import Link from "~/components/Link"

type PopoverContent = (closePopover: () => void) => ReactNode

const HeaderPopoverLink: FC<{
  title: string
  href?: string
  popover?: PopoverContent
}> = ({ title, href, popover }) => {
  const linkTriggerRef = useRef<HTMLAnchorElement>(null)
  const buttonTriggerRef = useRef<HTMLButtonElement>(null)
  const timeOutRef = useRef<NodeJS.Timeout | null>(null)

  const handleEnter = (isOpen: boolean) => {
    if (timeOutRef.current !== null) {
      clearTimeout(timeOutRef.current)
    }

    if (!isOpen) {
      const triggerElement = href
        ? linkTriggerRef.current
        : buttonTriggerRef.current
      triggerElement?.click()
    }
  }

  const handleClose = (isOpen: boolean) => {
    if (isOpen) {
      const triggerElement = href
        ? linkTriggerRef.current
        : buttonTriggerRef.current
      triggerElement?.click()
    }
  }

  const buttonClassName =
    "cursor-pointer hover:bg-[#193479] font-serif rounded-full py-1 px-3 text-base"

  return (
    <Popover className="flex h-16 items-center justify-center lg:h-14">
      {({ open }) => (
        <div
          onMouseEnter={() => handleEnter(open)}
          onMouseLeave={() => handleClose(open)}
        >
          {href ? (
            <Popover.Button
              as={Link}
              to={href}
              // Type cast needed due to Headless UI limitation: Popover.Button expects
              // a ref matching its default element type, but when using as={Link},
              // we need an HTMLAnchorElement ref. This is safe as Link forwards refs correctly.
              ref={linkTriggerRef as any}
              className={classNames(buttonClassName, {
                "bg-[#193479]": open
              })}
            >
              {title}
            </Popover.Button>
          ) : (
            <Popover.Button
              ref={buttonTriggerRef}
              className={classNames(buttonClassName, {
                "bg-[#193479]": open
              })}
            >
              {title}
            </Popover.Button>
          )}
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 transform"
            enterTo="opacity-100 transform"
            leave="transition ease-in duration-200"
            leaveFrom="opacity-100 transform"
            leaveTo="opacity-0 transform"
          >
            <Popover.Panel className="absolute left-0 top-14 z-40 overflow-hidden rounded-b-3xl bg-white shadow-[0px_0px_20px_rgba(0,0,0,0.1)]">
              {popover && popover(() => handleClose(open))}
            </Popover.Panel>
          </Transition>
        </div>
      )}
    </Popover>
  )
}

export default HeaderPopoverLink
